import React from 'react';
import { Navigate } from 'react-router-dom';
import { useProgressiveAuth } from '@/hooks/auth/useProgressiveAuth';

/**
 * Simple redirector component for the index route
 * Redirects to appropriate workspace based on user permissions
 */
export const SimpleRedirector: React.FC = () => {
  const { 
    user,
    isReady,
    isFullyLoaded,
    hasInternalAccess, 
    hasCompanyAccess, 
    hasRecruiterAccess, 
    hasTalentAccess,
    hasNannyAccess,
    hasMotherAccess
  } = useProgressiveAuth();

  // Wait for auth to be ready
  if (!isReady || !isFullyLoaded) {
    return null; // PreRouteGate will handle the loading
  }

  // If no user, redirect to auth
  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  // Get preferred workspace from localStorage or use intelligent defaults
  const getPreferredWorkspace = () => {
    const storedMode = localStorage.getItem('workspace_mode');
    const selectedCompanyId = localStorage.getItem('selectedCompanyId');
    
    // If user has a stored preference and access to that workspace, use it
    if (storedMode === 'nanny' && hasNannyAccess) {
      return '/workspace/nanny/dashboard';
    }
    if (storedMode === 'mother' && hasMotherAccess) {
      return '/workspace/mother/dashboard';
    }
    if (storedMode === 'talent' && hasTalentAccess) {
      return '/workspace/talent/jobs';
    }
    // Only go to company if we actually have a selected company
    if (storedMode === 'company' && hasCompanyAccess && selectedCompanyId) {
      return '/workspace/company/hiring-center';
    }
    if (storedMode === 'recruiter' && hasRecruiterAccess) {
      return '/workspace/recruiter/network';
    }
    if (storedMode === 'internal' && hasInternalAccess) {
      return '/workspace/internal/dashboard';
    }
    
    // No stored preference or no access to stored preference, use priority order
    // Nanny/mother first as separate platforms
    if (hasNannyAccess) {
      return '/workspace/nanny/dashboard';
    }
    if (hasMotherAccess) {
      return '/workspace/mother/dashboard';
    }
    if (hasTalentAccess) {
      return '/workspace/talent/jobs';
    }
    if (hasCompanyAccess) {
      return '/workspace/company/hiring-center';
    }
    if (hasRecruiterAccess) {
      return '/workspace/recruiter/network';
    }
    if (hasInternalAccess) {
      return '/workspace/internal/dashboard';
    }
    
    return '/evee-ai';
  };

  return <Navigate to={getPreferredWorkspace()} replace />;

  // No access, redirect to auth
  return <Navigate to="/auth" replace />;
};